/*
 * Identity — the verified-session snapshot the authz layer reads. SHARED across
 * every product UI (the gateway app + fleet-admin both gate off the SAME catalog,
 * mirroring the backend authz — vitesse-trust/authz/authz.go).
 *
 * Decoupled from the concrete `@clerk/clerk-js` type via `IdentityHost` so base-ui
 * needs NO Clerk dependency; the real Clerk singleton satisfies it structurally.
 * The name is IdP-NEUTRAL on purpose: naming the interface after one vendor is
 * what makes a provider swap look like a rewrite of every consumer.
 *
 * LOAD-BEARING: UX gating ONLY (hide/disable). The real "no" is the server
 * rejecting the call (PermissionDenied) — never trust the client for authz.
 */

/** The minimal shape identity/org resolution needs — the real Clerk singleton
 *  satisfies it structurally. Nothing here may name a vendor. */
export interface IdentityHost {
  user?: {
    id: string;
    organizationMemberships: ReadonlyArray<{
      organization: { id: string; name?: string };
      role: string;
      permissions?: ReadonlyArray<string>;
    }>;
  } | null;
  organization?: { id: string; name?: string } | null;
  /** Subscribe to session changes. Optional because `identityFrom` reads a
   *  snapshot and does not need it — `bindIdentity` does, since an org switch or a
   *  sign-out has to reach the UI, and without it the app keeps rendering the
   *  permissions of the org the user just left. */
  addListener?: (fn: () => void) => unknown;
}

/** The active-org identity snapshot, derived from the verified session. */
export interface Identity {
  /** IdP user id ('' when signed out). */
  userId: string;
  /** Active organization id ('' when no org is active / personal account). */
  orgId: string;
  /** Human-friendly active-org name — '' when none. Consumers fall back to the
   *  id when it is absent, so a host that cannot supply one still works. */
  orgName: string;
  /** Caller's role in the active org, normalized to 'admin' | 'member' | other
   *  (an 'org:'-prefixed role has the prefix stripped — see normalizeRole). */
  role: string;
  /** Explicit org permissions on the membership, in `org:<feature>:<action>`
   *  form (Clerk's `org_permissions` claim). */
  permissions: ReadonlySet<string>;
  /** The fleet-authored per-user grant matrix: grantKey → allowed. Absent =
   *  nothing authored — role defaults decide. See authz.canFrom. Supplied by
   *  `setGrantMatrixProvider`, NOT by the IdP. */
  grants?: Readonly<Record<string, boolean>>;
}

/** Signed-out / unknown identity — grants nothing. */
export const ANON: Identity = { userId: '', orgId: '', orgName: '', role: '', permissions: new Set() };

/** Strip the 'org:' role prefix → 'admin' | 'member' | <other>. Mirrors
 *  authz.go normalizeRole.
 *
 *  A provider may emit a BARE 'admin', in which case this is a no-op. Kept
 *  because the prefixed spelling is the catalog's key shape (see authz.Perm): a
 *  stored grant or a fixture may carry 'org:admin', and normalising here is what
 *  stops that reading as an unknown role — which grants nothing, silently. */
export function normalizeRole(role: string): string {
  return role.startsWith('org:') ? role.slice('org:'.length) : role;
}

/**
 * Read the current identity from the identity host. The active org is
 * `host.organization`; the caller's role + explicit permissions come from the
 * membership for that org. Returns ANON-ish when signed out or no org is active.
 */
export function identityFrom(host: IdentityHost | undefined): Identity {
  const user = host?.user;
  const org = host?.organization;
  const orgId = org?.id ?? '';
  if (!user || !orgId) {
    return { userId: user?.id ?? '', orgId, orgName: org?.name ?? '', role: '', permissions: new Set() };
  }
  const membership = user.organizationMemberships.find((m) => m.organization.id === orgId);
  return {
    userId: user.id,
    orgId,
    orgName: org?.name ?? '',
    role: normalizeRole(membership?.role ?? ''),
    permissions: new Set<string>(membership?.permissions ?? []),
    grants: grantMatrixProvider(user.id, orgId),
  };
}

/*
 * THE AUTHORED MATRIX — now injected, because the IdP stopped carrying it.
 *
 * `canFrom` consults `id.grants` before the role catalog. The matrix used to ride
 * on the IdP's own membership metadata, projected into a session claim — one
 * value, two readers, so the server's refusal and the browser's greyed control
 * could not disagree.
 *
 * THE MATRIX NO LONGER RIDES THE IdP — vendor membership metadata is not a source
 * that survives a provider swap. It lives in our own fabric (fleet_usergrants KV),
 * is enforced by the api-gateway from its own cache of that bucket, and reaches
 * the browser on SessionEntitlements.Subscribe — the same per-caller stream the
 * plan rides, served from the same cache the gate reads. base-ui's session.ts
 * installs the provider (`setAuthoredGrants`) and feeds it from that stream.
 *
 * The default provider answers "nothing authored", which is what an absent
 * matrix has always meant: role defaults decide. Read the failure mode before
 * changing that. It is what a UI shows between boot and the first frame, and
 * what a host that never installs a provider (a test, a worker) gets forever: a
 * deliberate revocation is REINSTATED in the UI for that window — the browser
 * offers a control the server will refuse. That is UX-only (the server still says
 * no) and strictly better than defaulting to deny, which would tell every paying
 * customer they own nothing every time a stream was slow to connect. The stream
 * keeps the window to one connect; a provider that is never installed is a
 * wiring bug in the host, not a resting state.
 *
 * The provider is keyed by (userId, orgId) so it can be backed by a
 * per-membership lookup; the stream is per-caller, so today only the org is
 * checked.
 */

/** Supplies the authored grant matrix for a (user, org). Returning undefined
 *  means "nothing authored" — role defaults decide. */
export type GrantMatrixProvider = (userId: string, orgId: string) => Record<string, boolean> | undefined;

let grantMatrixProvider: GrantMatrixProvider = () => undefined;

/**
 * Install the grant-matrix source. Call ONCE at boot, before anything renders.
 *
 * Synchronous by design: `identityFrom` is a snapshot read on every session tick,
 * so the provider must answer from an already-loaded cache, never by starting an
 * RPC. The caller owns the fetch and re-notifies the session when it lands.
 */
export function setGrantMatrixProvider(fn: GrantMatrixProvider): void {
  grantMatrixProvider = fn;
}

/**
 * Normalise a raw matrix, as served by fleet-control, to the boolean map `canFrom`
 * reads.
 *
 * Tolerance is PER ENTRY, matching the server-side decoder: one malformed entry must
 * not discard the entries beside it, because discarding them silently reinstates
 * every permission an administrator took away.
 */
export function normalizeGrants(raw: unknown): Record<string, boolean> | undefined {
  if (raw == null) return undefined;
  // Present but not an object: nothing to salvage per entry, and pretending it
  // said nothing would reinstate whatever it revoked.
  if (typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const out: Record<string, boolean> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value === 'boolean') out[key] = value;
    // A key whose value is not a boolean is dropped, and the rest survive.
  }
  return out;
}

/**
 * Map of orgId → human name, across the user's memberships + the active org.
 * Names only resolve for orgs the user BELONGS to — a super-admin viewing another
 * org's gateways won't have its name here, so callers fall back gracefully (never
 * show a raw id). The single source of org names for the whole UI.
 */
export function orgNamesFrom(host: IdentityHost | undefined): Map<string, string> {
  const m = new Map<string, string>();
  const active = host?.organization;
  if (active?.id && active.name) m.set(active.id, active.name);
  for (const mem of host?.user?.organizationMemberships ?? []) {
    if (mem.organization.name) m.set(mem.organization.id, mem.organization.name);
  }
  return m;
}

/** Back-compat alias for the pre-rename spelling. `IdentityHost` is the name;
 *  this exists so a consumer that still imports `ClerkLike` keeps compiling. */
export type ClerkLike = IdentityHost;
